import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiTrendingUp,
  FiTrendingDown,
  FiEdit,
  FiFilter,
  FiPlus,
  FiSearch,
  FiAlertTriangle,
  FiGrid,
  FiList,
  FiColumns,
  FiDownload,
  FiSave,
  FiX
} from 'react-icons/fi';
import { inventarioService, productosService, reportesService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import MovimientoStockModal from '../../components/Inventario/MovimientoStockModal';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';

const Inventario = () => {
  const navigate = useNavigate();

  // Estados
  const [activeTab, setActiveTab] = useState('stock'); // Cambiado a 'stock' como principal
  const [inventario, setInventario] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // Vista por defecto: tabla

  // Filtros para inventario
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [outOfStockOnly, setOutOfStockOnly] = useState(false);

  // Modal de movimiento
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Estados para edición inline
  const [editingProductId, setEditingProductId] = useState(null);
  const [editedMinStock, setEditedMinStock] = useState('');
  const [editedMaxStock, setEditedMaxStock] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Estados para descarga de reportes
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    loadData();
  }, [activeTab, includeInactive, lowStockOnly, outOfStockOnly]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (activeTab === 'stock') {
        // Cargar inventario actual con filtros
        const result = await inventarioService.getInventory({
          include_inactive: includeInactive,
          low_stock_only: lowStockOnly,
          out_of_stock_only: outOfStockOnly,
          limit: 100
        });
        if (result.success) {
          // Mapear los campos de la API a los campos que el componente espera
          const inventoryData = result.data?.inventory || [];
          const mappedInventory = inventoryData.map(item => ({
            ...item,
            code: item.product_code,
            sku: item.product_code,
            stock: item.current_stock,
            stock_min: item.min_stock,
            stock_max: item.max_stock,
            unit_cost: item.cost_price,
            is_active: item.status === 'ACTIVE'
          }));
          setInventario(mappedInventory);
        }
      } else if (activeTab === 'movimientos') {
        const result = await inventarioService.getMovements({ limit: 50 });
        if (result.success) {
          setMovimientos(result.data?.data || []);
        }
      }

      // Cargar estadísticas
      const statsResult = await inventarioService.getStats();
      if (statsResult.success) {
        setStats(statsResult.data?.data || statsResult.data);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAjustarStock = (productId = null) => {
    if (productId) {
      navigate(`/system/inventario/ajustar?product_id=${productId}`);
    } else {
      navigate('/system/inventario/ajustar');
    }
  };

  const handleOpenModal = (product, e) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleMovimientoSuccess = (updatedProduct) => {
    // Recargar datos después de un movimiento exitoso
    loadData();
  };

  const getStockStatus = (product) => {
    if (product.stock === 0) {
      return { label: 'AGOTADO', className: 'danger' };
    } else if (product.stock <= product.stock_min) {
      return { label: 'BAJO', className: 'warning' };
    } else {
      return { label: 'OK', className: 'success' };
    }
  };

  const getOperationIcon = (movementType) => {
    if (movementType?.includes('add') || movementType?.includes('entrada')) {
      return <FiTrendingUp className="text-success" />;
    }
    return <FiTrendingDown className="text-danger" />;
  };

  const getOperationLabel = (movementType) => {
    const labels = {
      'add': 'Entrada',
      'subtract': 'Salida',
      'set': 'Ajuste'
    };
    return labels[movementType] || movementType;
  };

  const handleEditStock = (item, e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingProductId(item.product_id);
    setEditedMinStock(item.stock_min || 0);
    setEditedMaxStock(item.stock_max || 0);
  };

  const handleCancelEdit = (e) => {
    if (e) {
      e.stopPropagation();
    }
    setEditingProductId(null);
    setEditedMinStock('');
    setEditedMaxStock('');
  };

  const handleSaveStock = async (productId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setIsSaving(true);
    setError(null);

    try {
      const updateData = {
        min_stock: parseFloat(editedMinStock) || 0,
        max_stock: parseFloat(editedMaxStock) || 0
      };

      const result = await productosService.updateProducto(productId, updateData);

      if (result.success) {
        // Actualizar el inventario local
        setInventario(prevInventario =>
          prevInventario.map(item =>
            item.product_id === productId
              ? { ...item, stock_min: updateData.min_stock, stock_max: updateData.max_stock }
              : item
          )
        );
        setEditingProductId(null);
        setEditedMinStock('');
        setEditedMaxStock('');
      } else {
        setError(result.message || 'Error al actualizar stock');
      }
    } catch (error) {
      console.error('Error al guardar stock:', error);
      setError('Error al guardar los cambios');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        include_inactive: includeInactive,
        low_stock_only: lowStockOnly,
        out_of_stock_only: outOfStockOnly
      };

      const result = await reportesService.exportarInventario(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `inventario_${startDate}_${endDate}.${extensions[format]}`
          : `inventario_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setShowDownloadModal(false);
      } else {
        setError(result.message || 'Error al generar el reporte');
      }
    } catch (err) {
      console.error('Error al descargar reporte:', err);
      setError('Error al descargar el reporte');
    } finally {
      setIsDownloading(false);
    }
  };

  const filteredInventario = inventario.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.product_name?.toLowerCase().includes(search) ||
      item.code?.toLowerCase().includes(search) ||
      item.sku?.toLowerCase().includes(search) ||
      item.product_code?.toLowerCase().includes(search)
    );
  });

  const filteredMovimientos = movimientos.filter(mov => {
    const matchesSearch = !searchTerm ||
      mov.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mov.reason?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (isLoading && inventario.length === 0 && movimientos.length === 0) {
    return <LoadingGrid message="Cargando inventario..." />;
  }

  return (
    <div className="inventario-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiAlertTriangle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">INVENTARIOss</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button
            className="menu-item menu-item--first"
            onClick={() => handleAjustarStock()}
          >
            + AJUSTAR STOCK
          </button>

          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder={activeTab === 'stock' ? "PRODUCTO, CÓDIGO O SKU" : "PRODUCTO O RAZÓN"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR REPORTE ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Tabs integradas */}
      <div className="inventory-tabs">
        <button
          className={`inventory-tab ${activeTab === 'stock' ? 'active' : ''}`}
          onClick={() => setActiveTab('stock')}
        >
          <FiPackage /> STOCK ACTUAL
        </button>
        <button
          className={`inventory-tab ${activeTab === 'movimientos' ? 'active' : ''}`}
          onClick={() => setActiveTab('movimientos')}
        >
          <FiTrendingUp /> MOVIMIENTOS
        </button>
      </div>

      {/* Filtros adicionales solo para vista de stock */}
      {activeTab === 'stock' && (
        <div className="inventory-filters">
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={lowStockOnly}
              onChange={(e) => {
                setLowStockOnly(e.target.checked);
                if (e.target.checked) setOutOfStockOnly(false);
              }}
            />
            <span>SOLO STOCK BAJO</span>
          </label>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={outOfStockOnly}
              onChange={(e) => {
                setOutOfStockOnly(e.target.checked);
                if (e.target.checked) setLowStockOnly(false);
              }}
            />
            <span>SOLO AGOTADOS</span>
          </label>
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            <span>INCLUIR INACTIVOS</span>
          </label>
        </div>
      )}

      {/* Contenido según tab activo */}
      {activeTab === 'stock' && (
        <>
          {filteredInventario.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FiPackage size={48} />
              </div>
              <h3 className="empty-state-title">
                {searchTerm || lowStockOnly || outOfStockOnly
                  ? 'No se encontraron productos'
                  : 'No hay productos en el inventario'}
              </h3>
              <p className="empty-state-description">
                {searchTerm || lowStockOnly || outOfStockOnly
                  ? 'No se encontraron productos con los filtros aplicados'
                  : 'Comienza agregando productos al inventario'}
              </p>
            </div>
          ) : viewMode === 'table' ? (
            <div className="table-container">
              <table className="data-table data-table--inventario">
                <thead>
                  <tr>
                    <th>PRODUCTO</th>
                    <th className="text-center">STOCK ACTUAL</th>
                    <th className="text-center">STOCK MÍNIMO</th>
                    <th className="text-center">STOCK MÁXIMO</th>
                    <th className="text-center">ESTADO</th>
                    <th className="text-right">COSTO UNIT.</th>
                    <th className="text-right">VALOR TOTAL</th>
                    <th className="text-center">ACCIONES</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventario.map((item) => {
                    const status = getStockStatus(item);
                    const isEditing = editingProductId === item.product_id;
                    return (
                      <tr key={item.product_id}>
                        <td>
                          <strong>{item.product_name}</strong>
                          {!item.is_active && (
                            <span className="badge badge--secondary ml-2">Inactivo</span>
                          )}
                        </td>
                        <td className="text-center">
                          <strong>{item.stock || 0}</strong>
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editedMinStock}
                              onChange={(e) => setEditedMinStock(e.target.value)}
                              min="0"
                              step="0.01"
                              style={{
                                width: '80px',
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                textAlign: 'center'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span>{item.stock_min || 0}</span>
                          )}
                        </td>
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editedMaxStock}
                              onChange={(e) => setEditedMaxStock(e.target.value)}
                              min="0"
                              step="0.01"
                              style={{
                                width: '80px',
                                padding: '4px 8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontSize: '13px',
                                textAlign: 'center'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span>{item.stock_max || 0}</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={`status-badge status-badge--${status.className}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="text-right">
                          ${(item.unit_cost || 0).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right">
                          <strong>
                            ${((item.stock || 0) * (item.unit_cost || 0)).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                        <td className="text-center">
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                className="btn btn--sm btn--success"
                                onClick={(e) => handleSaveStock(item.product_id, e)}
                                disabled={isSaving}
                                title="Guardar cambios"
                              >
                                <FiSave />
                              </button>
                              <button
                                className="btn btn--sm btn--ghost"
                                onClick={handleCancelEdit}
                                disabled={isSaving}
                                title="Cancelar"
                              >
                                <FiX />
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                className="btn btn--sm btn--secondary"
                                onClick={(e) => handleEditStock(item, e)}
                                title="Editar stock mín/máx"
                              >
                                <FiEdit />
                              </button>
                              <button
                                className="btn btn--sm btn--primary"
                                onClick={(e) => handleOpenModal(item, e)}
                                title="Realizar movimiento"
                              >
                                <FiPackage />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
              {filteredInventario.map((item) => {
                const status = getStockStatus(item);
                return (
                  <div
                    key={item.product_id}
                    className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
                    onClick={() => handleAjustarStock(item.product_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    {viewMode === 'grid' ? (
                      <>
                        <div className={`content-thumbnail ${getThumbnailType(status.className)}`}>
                          <div className="inventory-label">
                            {item.product_name}
                          </div>
                          <div className="inventory-stock">
                            {item.stock || 0}
                          </div>
                        </div>
                        <div className="content-info">
                          <div className="content-title">{item.code || item.sku || 'Sin código'}</div>
                          <div className="content-subtitle">Stock mín: {item.stock_min || 0}</div>
                          <div className="content-meta">
                            <span className={`status-badge status-badge--${status.className}`}>
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={`content-thumbnail-small ${getThumbnailType(status.className)}`}>
                          <FiPackage className="thumbnail-icon" />
                        </div>
                        <div className="content-info-list">
                          <div className="content-title">{item.product_name}</div>
                          <div className="content-subtitle">{item.code || item.sku || 'Sin código'}</div>
                          <div className="content-meta">
                            <span className="meta-item">
                              Stock: <strong>{item.stock || 0}</strong>
                            </span>
                            <span className="meta-item">
                              Mín: {item.stock_min || 0}
                            </span>
                            <span className={`status-badge status-badge--${status.className}`}>
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Modal de Movimiento de Stock */}
      <MovimientoStockModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        producto={selectedProduct}
        onSuccess={handleMovimientoSuccess}
      />

      {activeTab === 'movimientos' && (
        <div className="table-container">
          <table className="data-table data-table--movimientos">
            <thead>
              <tr>
                <th>FECHA</th>
                <th>PRODUCTO</th>
                <th>TIPO</th>
                <th className="text-right">CANTIDAD</th>
                <th>RAZÓN</th>
                <th>USUARIO</th>
                <th>NOTAS</th>
              </tr>
            </thead>
            <tbody>
              {filteredMovimientos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No hay movimientos registrados
                  </td>
                </tr>
              ) : (
                filteredMovimientos.map((mov, index) => (
                  <tr key={mov.id || index}>
                    <td>{new Date(mov.created_at || mov.movement_date).toLocaleDateString()}</td>
                    <td><strong>{mov.product_name || `Producto #${mov.product_id}`}</strong></td>
                    <td>
                      <span className={`status-badge status-badge--${mov.movement_type === 'add' ? 'success' : mov.movement_type === 'subtract' ? 'danger' : 'info'}`}>
                        {getOperationIcon(mov.movement_type)} {getOperationLabel(mov.movement_type)}
                      </span>
                    </td>
                    <td className="text-right"><strong>{mov.quantity_change || mov.quantity}</strong></td>
                    <td>{mov.reason || '-'}</td>
                    <td>{mov.user_name || mov.created_by || '-'}</td>
                    <td>{mov.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Inventario"
        moduleName="inventario"
        isLoading={isDownloading}
      />
    </div>
  );

  // Funciones helper
  function getThumbnailType(statusClass) {
    switch (statusClass) {
      case 'success':
        return 'blue-dark-gradient'; // Stock OK = Morado
      case 'warning':
        return 'blue-dark-gradient'; // Stock Bajo = Morado
      case 'danger':
        return 'blue-dark-gradient'; // Stock Agotado = Morado
      default:
        return 'blue-dark-gradient';
    }
  }
};

export default Inventario;
